import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "#/components/shared/card";
import Input from "#/components/shared/input";
import Typography from "#/components/shared/typography";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import { useToast } from "#/hooks/ToastContext";
import useMedicalConceptsList from "#/admin/medical-concepts/hooks/useMedicalConceptsList";
import useMedicalConceptMutations from "#/admin/medical-concepts/hooks/useMedicalConceptMutations";

type MedicalConceptType =
  | "disease"
  | "symptom"
  | "treatment"
  | "pathophysiology"
  | "diagnosis"
  | "procedure"
  | "medication"
  | "anatomy"
  | "biomarker"
  | "risk_factor";

const conceptTypes: MedicalConceptType[] = [
  "disease",
  "symptom",
  "treatment",
  "pathophysiology",
  "diagnosis",
  "procedure",
  "medication",
  "anatomy",
  "biomarker",
  "risk_factor",
];

export function MedicalConceptsLayout() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { createConcept } = useMedicalConceptMutations();

  const [conceptQuery, setConceptQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [newConceptName, setNewConceptName] = useState("");
  const [newConceptType, setNewConceptType] =
    useState<MedicalConceptType>("disease");
  const [newConceptDescription, setNewConceptDescription] = useState("");

  const conceptsQuery = useMedicalConceptsList({
    query: appliedQuery,
    limit: 100,
  });

  const handleLoadConcepts = async () => {
    setAppliedQuery(conceptQuery.trim());
    await conceptsQuery.refetch();
  };

  const handleCreateConcept = async () => {
    const name = newConceptName.trim();
    if (!name) {
      showToast("Concept name is required.", "error");
      return;
    }

    try {
      await createConcept.mutateAsync({
        name,
        concept_type: newConceptType,
        description: newConceptDescription.trim() || null,
      });

      showToast("Medical concept created.", "success");
      setNewConceptName("");
      setNewConceptDescription("");
      conceptsQuery.refetch();
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : "Failed to create concept.",
        "error"
      );
    }
  };

  const conceptOptions = Array.isArray(conceptsQuery.data) ? conceptsQuery.data : [];

  return (
    <div className="flex flex-col gap-6">
      <Card className="w-full" variant="default" elevation={1}>
        <Card.Header divider>
          <div className="flex flex-col gap-1">
            <Typography variant="h4" className="text-brown-900">
              Medical concepts
            </Typography>
            <Typography variant="body2" className="text-brown-600">
              Browse concepts. Open any concept for details and generation actions.
            </Typography>
          </div>
        </Card.Header>
        <Card.Body className="bg-white">
          <div className="flex flex-wrap items-end gap-3">
            <Input
              id="medical-concept-search"
              label="Concept search"
              placeholder="e.g. pancreatic cancer"
              value={conceptQuery}
              onChange={(event) => setConceptQuery(event.target.value)}
              containerClassName="min-w-[320px]"
            />
            <ActionButton
              size="sm"
              onClick={handleLoadConcepts}
              isLoading={conceptsQuery.isFetching}
            >
              Refresh list
            </ActionButton>
          </div>

          <div className="mt-4 overflow-x-auto">
            {conceptsQuery.isLoading ? (
              <Typography variant="body2" className="text-brown-600">
                Loading concepts…
              </Typography>
            ) : conceptsQuery.isError ? (
              <Typography variant="body2" className="text-terracotta">
                {conceptsQuery.error instanceof Error
                  ? conceptsQuery.error.message
                  : "Unable to load concepts."}
              </Typography>
            ) : conceptOptions.length === 0 ? (
              <Typography variant="body2" className="text-brown-600">
                No concepts found.
              </Typography>
            ) : (
              <table className="min-w-full text-left text-sm">
                <thead className="bg-cream-50 text-brown-700">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">Type</th>
                    <th className="px-4 py-3 font-semibold">Description</th>
                    <th className="px-4 py-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-beige-100">
                  {conceptOptions.map((item) => (
                    <tr key={item.id}>
                      <td className="px-4 py-3 text-brown-900">{item.name}</td>
                      <td className="px-4 py-3 text-brown-700">
                        {item.concept_type ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-brown-700">
                        {item.description?.trim() || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <ActionButton
                          size="sm"
                          tone="secondary"
                          onClick={() =>
                            navigate(`/admin/content/medical-concepts/${item.id}`)
                          }
                        >
                          View details
                        </ActionButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card.Body>
      </Card>

      <Card className="w-full" variant="default" elevation={1}>
        <Card.Header divider>
          <Typography variant="h4" className="text-brown-900">
            Create medical concept
          </Typography>
        </Card.Header>
        <Card.Body className="bg-white">
          <div className="grid gap-3 md:grid-cols-3">
            <Input
              id="new-concept-name"
              label="Name"
              placeholder="Concept name"
              value={newConceptName}
              onChange={(event) => setNewConceptName(event.target.value)}
            />
            <div>
              <label
                htmlFor="new-concept-type"
                className="mb-1 block text-sm font-medium text-brown-700"
              >
                Type
              </label>
              <select
                id="new-concept-type"
                className="w-full rounded-md border border-beige-300 bg-white px-3 py-2 text-sm text-brown-900"
                value={newConceptType}
                onChange={(event) =>
                  setNewConceptType(event.target.value as MedicalConceptType)
                }
              >
                {conceptTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <Input
              id="new-concept-description"
              label="Description"
              placeholder="Optional"
              value={newConceptDescription}
              onChange={(event) => setNewConceptDescription(event.target.value)}
            />
          </div>
          <div className="mt-3">
            <ActionButton
              size="sm"
              onClick={handleCreateConcept}
              isLoading={createConcept.isPending}
              disabled={createConcept.isPending}
            >
              Create concept
            </ActionButton>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

export default MedicalConceptsLayout;
